import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";

import { AnswerValue, Assessment, AssessmentsDocument } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import { resolveChoiceSet } from "./assessmentsMutations";
import { MultipleChoiceAnswerField } from "./MultipleChoiceAnswerField";
import { ScaleAnswerField } from "./ScaleAnswerField";
import { TextAnswerField } from "./TextAnswerField";

interface AssessmentPreviewModalProps {
    open: boolean;
    assessment: Assessment | null;
    document: AssessmentsDocument;
    onOpenChange: (open: boolean) => void;
}

export function AssessmentPreviewModal({
    open,
    assessment,
    document,
    onOpenChange
}: AssessmentPreviewModalProps) {
    const { t } = useTranslation();
    const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});

    useEffect(() => {
        if (open) {
            setAnswers({});
        }
    }, [open, assessment?.id]);

    if (!assessment) {
        return null;
    }

    const setAnswer = (questionId: string, value: AnswerValue) => {
        setAnswers(previous => ({ ...previous, [questionId]: value }));
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={`${styles.dialogContent} ${styles.dialogContentWide}`}>
                    <Dialog.Title className={styles.dialogTitle}>{assessment.title}</Dialog.Title>
                    {assessment.description ? (
                        <Dialog.Description className={styles.previewDescription}>
                            {assessment.description}
                        </Dialog.Description>
                    ) : null}

                    {assessment.questions.length === 0 ? (
                        <p className={styles.emptyState}>{t("settingsPage.company.assessments.noQuestions")}</p>
                    ) : (
                        assessment.questions.map(question => (
                            <div key={question.id} className={styles.previewQuestion}>
                                <p className={styles.previewPrompt}>{question.prompt}</p>
                                {question.type === "scale" ? (
                                    <ScaleAnswerField
                                        groupName={`scale-${question.id}`}
                                        min={question.min}
                                        max={question.max}
                                        minLabel={question.min_label}
                                        maxLabel={question.max_label}
                                        value={
                                            typeof answers[question.id] === "number"
                                                ? (answers[question.id] as number)
                                                : undefined
                                        }
                                        onChange={value => setAnswer(question.id, value)}
                                    />
                                ) : null}
                                {question.type === "text" ? (
                                    <TextAnswerField
                                        value={
                                            typeof answers[question.id] === "string"
                                                ? (answers[question.id] as string)
                                                : ""
                                        }
                                        onChange={value => setAnswer(question.id, value)}
                                    />
                                ) : null}
                                {question.type === "multiple_choice" ? (
                                    (() => {
                                        const choiceSet = resolveChoiceSet(document, question.choice_set_id);
                                        if (!choiceSet) {
                                            return (
                                                <p className={styles.emptyState}>
                                                    {t("settingsPage.company.assessments.missingChoiceSet")}
                                                </p>
                                            );
                                        }
                                        return (
                                            <MultipleChoiceAnswerField
                                                groupName={`mc-${question.id}`}
                                                choiceSet={choiceSet}
                                                value={
                                                    typeof answers[question.id] === "string"
                                                        ? (answers[question.id] as string)
                                                        : undefined
                                                }
                                                onChange={value => setAnswer(question.id, value)}
                                            />
                                        );
                                    })()
                                ) : null}
                            </div>
                        ))
                    )}

                    <div className={styles.dialogActions}>
                        <div />
                        <div className={styles.dialogPrimaryActions}>
                            <Dialog.Close asChild>
                                <button type="button" className={styles.dialogCancelButton}>
                                    {t("settingsPage.account.cancel")}
                                </button>
                            </Dialog.Close>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
